// ============================================================
// TMR Portal Admin — reprovar uma solicitação de cadastro (28 §9)
// ============================================================
import { useState } from 'react';
import { ApiError } from '@tmr/api-client';
import { Btn } from '../../components/primitives';
import { ModalShell } from '../../components/ModalShell';
import { Notice } from '../../components/Notice';
import { useRejectCompanyApplication } from '../../data/companyApplications/queries';
import type { CompanyApplicationDetail } from '../../data/companyApplications/contract';

/** Props for {@link RejectApplicationDialog}. */
interface RejectApplicationDialogProps {
  application: Pick<CompanyApplicationDetail, 'id' | 'protocol' | 'clinic'>;
  onClose: () => void;
}

/**
 * Rejects an application with a reason. The reason is required — it is what the next person reading the
 * queue has to go on — and the rejection is final; nothing was ever sent to the legacy system.
 */
export function RejectApplicationDialog({ application, onClose }: RejectApplicationDialogProps) {
  const reject = useRejectCompanyApplication(application.id);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const missing = !reason.trim();

  const submit = () => {
    setTouched(true);
    if (missing) return;
    reject.mutate(reason.trim(), { onSuccess: onClose });
  };

  return (
    <ModalShell
      title="Reprovar solicitação"
      lead={`${application.clinic.tradeName} · ${application.protocol}. A solicitação sai da fila e não pode ser reaberta.`}
      onClose={onClose}
    >
      <label className="pa-form-label" htmlFor="reject-reason">
        Motivo <span style={{ color: 'var(--danger)' }}>*</span>
      </label>
      <textarea
        id="reject-reason"
        className="pa-input"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        rows={4}
        maxLength={1000}
        disabled={reject.isPending}
        placeholder="Por que a TMR não vai credenciar esta clínica agora."
        style={{
          width: '100%',
          padding: '9px 12px',
          fontFamily: 'var(--font-sans)',
          fontSize: 14,
          border: `1px solid ${touched && missing ? 'var(--danger)' : 'var(--border-default)'}`,
          borderRadius: 'var(--radius-sm)',
          resize: 'vertical',
        }}
      />
      {touched && missing && (
        <div style={{ marginTop: 4, fontSize: 12.5, color: 'var(--danger)' }}>Escreva o motivo antes de reprovar.</div>
      )}

      {reject.isError && (
        <Notice tone="danger">
          {reject.error instanceof ApiError ? reject.error.detail : 'Não foi possível reprovar a solicitação.'}
        </Notice>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
        <Btn variant="secondary" onClick={onClose} disabled={reject.isPending}>
          Cancelar
        </Btn>
        <Btn variant="primary" onClick={submit} disabled={reject.isPending}>
          {reject.isPending ? 'Reprovando…' : 'Reprovar'}
        </Btn>
      </div>
    </ModalShell>
  );
}
